import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Truck, MagnifyingGlass, ArrowsCounterClockwise,
  PlugsConnected, LinkBreak, CircleNotch,
} from '@phosphor-icons/react';

export default function VehicleControl() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | linked | free | trip

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/vehicle-control');
      setRows(data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 20000);
    return () => clearInterval(t);
  }, [fetchData]);

  const counts = useMemo(() => {
    const c = { all: rows.length, linked: 0, free: 0, trip: 0 };
    rows.forEach(v => {
      if (v.active_trip) c.trip++;
      if (v.is_free) c.free++;
      else c.linked++;
    });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(v => {
      if (statusFilter === 'free' && !v.is_free) return false;
      if (statusFilter === 'linked' && v.is_free) return false;
      if (statusFilter === 'trip' && !v.active_trip) return false;
      if (q) {
        const blob = `${v.vehicle_number} ${v.body_type} ${v.current_pilot_name} ${v.current_pilot_emp_id} ${v.current_pilot_mobile} ${v.secondary_pilot_name} ${v.secondary_pilot_mobile}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filter, statusFilter]);

  const handleDisconnect = async (vehicle) => {
    const linkedTo = vehicle.current_pilot_name || vehicle.secondary_pilot_name || 'no one';
    if (!window.confirm(
      `Disconnect ${vehicle.vehicle_number} from ${linkedTo}?\n\n` +
      `The pilot will lose access to this vehicle and another pilot can scan it.`
    )) return;
    setBusy(true);
    try {
      await api.put(`/admin/vehicle-control/${vehicle.id}/disconnect`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Disconnect failed');
    } finally { setBusy(false); }
  };

  const FILTERS = [
    { key: 'all', label: 'All Vehicles' },
    { key: 'linked', label: 'Pilot Linked' },
    { key: 'free', label: 'Free / Unlinked' },
    { key: 'trip', label: 'On Active Trip' },
  ];

  return (
    <div data-testid="vehicle-control-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Vehicle Control</h1>
          <p className="text-[#666] text-sm mt-1">
            See which pilot scanned which vehicle and force-release a vehicle so another pilot can take it · auto-refresh 20s
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="rounded-none" data-testid="refresh-vehicle-control-btn">
          <ArrowsCounterClockwise size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="vc-filters">
        {FILTERS.map(f => {
          const active = statusFilter === f.key;
          const count = counts[f.key] || 0;
          return (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`vc-filter-${f.key}`}
              className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                active ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#666] border-[#E5E5E5] hover:border-[#0A0A0A]'
              }`}
            >
              {f.label} <span className={active ? 'text-white/70' : 'text-[#0A0A0A] font-bold'}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search by vehicle number, body, pilot name, mobile..."
          data-testid="vc-search-input"
          className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-[#002FA7]"
        />
      </div>

      {loading ? (
        <div className="text-center text-[#666] py-12 text-sm flex items-center justify-center gap-2">
          <CircleNotch size={16} className="animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] p-12 text-center text-[#666] text-sm">
          No vehicles match these filters.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] divide-y divide-[#E5E5E5]" data-testid="vehicle-list">
          {filtered.map(v => (
            <div
              key={v.id}
              data-testid={`vehicle-row-${v.id}`}
              className="px-4 py-3 flex items-center gap-4 hover:bg-[#F7F7F7] transition-colors"
            >
              {/* Vehicle */}
              <div className="flex items-center gap-3 min-w-[180px] flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-[#0A0A0A]/5 flex items-center justify-center">
                  <Truck size={18} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <div className="font-mono text-sm font-bold tracking-tight" data-testid={`vc-veh-${v.id}`}>{v.vehicle_number}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#666] mt-0.5">
                    {v.body_type || '—'}{v.vehicle_type ? ` · ${v.vehicle_type}` : ''}
                  </div>
                </div>
              </div>

              {/* Linked pilot(s) */}
              <div className="flex-1 min-w-0">
                {v.is_free ? (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 bg-[#00C853]/10 text-[#00C853] font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00C853]" /> Free
                    </span>
                    <span className="text-xs text-[#666]">No pilot has scanned this vehicle</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {v.current_pilot_name && (
                      <div className="flex items-center gap-2 flex-wrap" data-testid={`vc-primary-${v.id}`}>
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#002FA7]/10 text-[#002FA7] font-bold">
                          <PlugsConnected size={10} /> Primary
                        </span>
                        <span className="text-sm font-medium">{v.current_pilot_name}</span>
                        <span className="text-[10px] text-[#666] uppercase tracking-widest">
                          {v.current_pilot_emp_id || '—'} · <span className="font-mono">{v.current_pilot_mobile}</span>
                        </span>
                      </div>
                    )}
                    {v.secondary_pilot_name && (
                      <div className="flex items-center gap-2 flex-wrap" data-testid={`vc-secondary-${v.id}`}>
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#FF8C00]/10 text-[#FF8C00] font-bold">
                          <PlugsConnected size={10} /> Secondary
                        </span>
                        <span className="text-sm font-medium">{v.secondary_pilot_name}</span>
                        <span className="text-[10px] text-[#666] uppercase tracking-widest font-mono">{v.secondary_pilot_mobile}</span>
                      </div>
                    )}
                    {v.active_trip && (
                      <div className="text-[10px] uppercase tracking-widest text-[#FF3333] font-bold flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF3333] animate-pulse" />
                        Live trip · {v.active_trip.status?.replace('_', ' ')} · {v.active_trip.pickup_supplier_name} → {v.active_trip.drop_supplier_name}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action */}
              <div className="flex-shrink-0">
                {v.is_free ? (
                  <span className="text-[10px] uppercase tracking-widest text-[#666]">—</span>
                ) : (
                  <Button
                    onClick={() => handleDisconnect(v)}
                    disabled={busy || !!v.active_trip}
                    title={v.active_trip ? 'Cancel/complete the active trip first' : 'Disconnect this vehicle from the pilot'}
                    className={`rounded-none text-xs uppercase tracking-widest h-9 ${
                      v.active_trip
                        ? 'bg-[#E5E5E5] text-[#999] cursor-not-allowed hover:bg-[#E5E5E5]'
                        : 'bg-[#FF3333] text-white hover:bg-[#FF3333]/90 hover:text-white'
                    }`}
                    data-testid={`disconnect-${v.id}`}
                  >
                    <LinkBreak size={13} className="mr-1.5" /> Disconnect
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help footer */}
      <div className="text-[11px] text-[#666] leading-relaxed">
        <strong className="text-[#0A0A0A]">Tip:</strong> Disconnecting a vehicle clears the primary <em>and</em> secondary pilot links, marks the vehicle as <code>available</code>, and lets any other pilot scan its QR code to take ownership.
        Vehicles with an <strong className="text-[#FF3333]">active trip</strong> can't be disconnected — cancel or complete the trip first.
      </div>
    </div>
  );
}
